import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchToken, fetchVideoToken } from '../redux/clientDataSlice';
import { fetchToken as fetchAttorneyToken, fetchVideoToken as fetchAttorneyVideoToken } from '../redux/attorneyDataSlice';

export const useTokenRefresh = (userType = 'client') => {
  const dispatch = useDispatch();
  const refreshTimeoutRef = useRef(null);
  
  // Get tokens and user data based on user type
  const token = useSelector(state => 
    userType === 'client' ? state.clientData?.token : state.attorneyData?.token
  );
  const videoToken = useSelector(state => 
    userType === 'client' ? state.clientData?.videoToken : state.attorneyData?.videoToken
  );
  const tokenExpiresAt = useSelector(state => 
    userType === 'client' ? state.clientData?.tokenExpiresAt : state.attorneyData?.tokenExpiresAt
  );
  const videoTokenExpiresAt = useSelector(state => 
    userType === 'client' ? state.clientData?.videoTokenExpiresAt : state.attorneyData?.videoTokenExpiresAt
  );
  const userId = useSelector(state => 
    userType === 'client' ? state.clientData?.clientData?.user_id : state.attorneyData?.attorneyData?.user_id
  );

  // Function to refresh tokens
  const refreshTokens = useCallback(async () => {
    if (!userId) return;
    
    try {
      if (userType === 'client') {
        await dispatch(fetchToken(userId)).unwrap();
        await dispatch(fetchVideoToken(userId)).unwrap();
      } else {
        await dispatch(fetchAttorneyToken(userId)).unwrap();
        await dispatch(fetchAttorneyVideoToken(userId)).unwrap();
      }
      console.log('Tokens refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh tokens:', error);
    }
  }, [dispatch, userId, userType]);

  // Function to schedule next token refresh
  const scheduleNextRefresh = useCallback(() => {
    if (!token || !videoToken || !tokenExpiresAt || !videoTokenExpiresAt) return;

    // Get current time
    const now = Math.floor(Date.now() / 1000);
    
    // Calculate time until tokens expire (refresh 5 minutes before expiration)
    const refreshBuffer = 5 * 60; // 5 minutes in seconds
    
    // Find the earliest expiration time
    const earliestExpiration = Math.min(tokenExpiresAt, videoTokenExpiresAt);
    const timeUntilRefresh = earliestExpiration - now - refreshBuffer;
    
    // If tokens expire very soon, refresh immediately
    if (timeUntilRefresh <= 0) {
      refreshTokens();
      return;
    }

    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule next refresh
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTokens();
    }, timeUntilRefresh * 1000);

    console.log(`Next token refresh scheduled in ${timeUntilRefresh} seconds`);
  }, [token, videoToken, tokenExpiresAt, videoTokenExpiresAt, refreshTokens]);

  // Effect to handle token refresh scheduling
  useEffect(() => {
    if (token && videoToken && userId && tokenExpiresAt && videoTokenExpiresAt) {
      scheduleNextRefresh();
    }

    // Cleanup timeout on unmount
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [token, videoToken, userId, tokenExpiresAt, videoTokenExpiresAt, scheduleNextRefresh]);

  // Effect to handle immediate refresh when tokens change
  useEffect(() => {
    if (token && videoToken && tokenExpiresAt && videoTokenExpiresAt) {
      scheduleNextRefresh();
    }
  }, [token, videoToken, tokenExpiresAt, videoTokenExpiresAt, scheduleNextRefresh]);

  // Function to manually refresh tokens (useful for error handling)
  const manualRefresh = useCallback(() => {
    refreshTokens();
  }, [refreshTokens]);

  return {
    manualRefresh,
    isRefreshing: false // You can add loading state if needed
  };
};
